import React from 'react';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    IconButton,
    CircularProgress
} from '@mui/material';

import * as ETVConstants from '../ETVConstants';
import { Add, Delete, Save } from '@mui/icons-material';

class IntercomDTMFDialog extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            saving: false,
            items: [],
            errors: {}
        };
    }

    componentDidMount() {
        if (this.props.open) {
            this.loadDTMF();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.loadDTMF();
        }
    }

    getCredentials = () => {
        return {
            accessToken: localStorage.getItem('etv.access_token'),
            reqUserID: localStorage.getItem('etv.user_id')
        };
    };

    getAuthHeaders = () => {
        const { accessToken, reqUserID } = this.getCredentials();

        return {
            user_id: reqUserID,
            access_token: accessToken
        };
    };

    loadDTMF = async () => {
        const { accessToken, reqUserID } = this.getCredentials();
        if (!reqUserID || !accessToken) {
            console.error('Missing user_id or access_token');
            return;
        }
        this.setState({ loading: true });
        try {
            const url = ETVConstants.getServerBase() + '/intercom_dtmf?action=list_dtmf&user_id=' + encodeURIComponent(reqUserID);
            const response = await fetch(url, {
                headers: this.getAuthHeaders()
            });
            const json = await response.json();
            if (!json.result) {
                console.error('DTMF load failed:', json.data);
                this.setState({ items: [] });
                return;
            }
            const list = Array.isArray(json.list) ? json.list : [];
            this.setState({
                items: list.map(item => ({
                    account_id: item.account_id,
                    dtmf_key: Number(item.dtmf_key || 0),
                    dtmf_action: item.dtmf_action || 'message',
                    value: item.value || ''
                }))
            });
        } catch (e) {
            console.error('DTMF load failed', e);
            this.setState({ items: [] });
        } finally {
            this.setState({ loading: false });
        }
    };

    addItem = () => {
        this.setState(prev => ({
            items: [
                ...prev.items,
                {
                    account_id: null,
                    dtmf_key: 1,
                    dtmf_action: 'message',
                    value: ''
                }
            ]
        }));
    };

    removeItem = async index => {
        const item = this.state.items[index];
        // If it is only a new unsaved row, remove locally
        if (!item || !item.dtmf_key) {
            this.setState(prev => ({
                items: prev.items.filter((_, i) => i !== index)
            }));
            return;
        }
        this.setState({ saving: true });
        try {
            await this.deleteItem(item);
            this.setState(prev => ({
                items: prev.items.filter((_, i) => i !== index)
            }));
        } catch (e) {
            console.error('DTMF delete failed', e);
        } finally {
            this.setState({ saving: false });
        }
    };

    validateItems = () => {
        const errors = {};
        const usedKeys = new Set();
        this.state.items.forEach((item, index) => {
            errors[index] = {};
            if (!item.dtmf_key) {
                errors[index].dtmf_key = ETVConstants.trans("required");
            }
            if (usedKeys.has(item.dtmf_key)) {
                errors[index].dtmf_key = ETVConstants.trans("must_be_unique");
            }
            usedKeys.add(item.dtmf_key);
            if (!item.dtmf_action) {
                errors[index].dtmf_action = ETVConstants.trans("required");
            }
            if (!item.value || item.value.trim() === '') {
                errors[index].value = ETVConstants.trans("required");
            }
        });

        const hasErrors =
            Object.values(errors).some(
                row => Object.keys(row).length > 0
            );
        this.setState({ errors });
        return !hasErrors;
    };   

    updateItem = (index, field, value) => {
        this.setState(prev => {
            const items = [...prev.items];
            items[index] = {
                ...items[index],
                [field]: field === 'dtmf_key' ? Number(value) : value
            };
            return { items };
        });
    };

    saveItem = async item => {
        const { reqUserID } = this.getCredentials();
        const url =
            ETVConstants.getServerBase() +
            '/intercom_dtmf?action=set_dtmf' +
            '&account_id=' + encodeURIComponent(reqUserID) +
            '&dtmf_key=' + encodeURIComponent(item.dtmf_key) +
            '&dtmf_action=' + encodeURIComponent(item.dtmf_action) +
            '&value=' + encodeURIComponent(item.value);

        const response = await fetch(url, {
            headers: this.getAuthHeaders()
        });
        const json = await response.json();
        if (!json.result) {
            throw new Error(json.data || 'Failed to save DTMF item');
        }
        return json;
    };

    saveAll = async () => {
        if (!this.validateItems()) {
            return;
        }
        const { accessToken, reqUserID } = this.getCredentials();
        if (!reqUserID || !accessToken) {
            console.error('Missing user_id or access_token');
            return;
        }
        this.setState({ saving: true });
        try {
            for (const item of this.state.items) {
                await this.saveItem(item);
            }
            await this.loadDTMF();
            if (this.props.onClose) {
                this.props.onClose();
            }
        } catch (e) {
            console.error('DTMF save failed', e);
            alert('Failed to save DTMF settings');
        } finally {
            this.setState({ saving: false });
        }
    };

    renderItem = (item, index) => {
        const error = this.state.errors[index] || {};
        return (
            <div
                key={index}
                style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    marginBottom: 14,
                    marginTop:10
                }}
            >

                <TextField
                    label="DTMF key"
                    type="number"
                    value={item.dtmf_key}
                    error={!!error.dtmf_key}
                    helperText={error.dtmf_key}
                    onChange={e =>
                        this.updateItem(index,'dtmf_key',e.target.value)
                    }
                    sx={{
                        width:80,
                        minWidth:80
                    }}
                />

                <TextField
                    select
                    label="Action"
                    value={item.dtmf_action}
                    error={!!error.dtmf_action}
                    helperText={error.dtmf_action}
                    onChange={e =>
                        this.updateItem(index,'dtmf_action',e.target.value)
                    }
                    sx={{
                        width:120,
                        minWidth:120
                    }}
                >
                    <MenuItem value="message"> {ETVConstants.trans("message")} </MenuItem>
                </TextField>

                <TextField
                    label="Value"
                    value={item.value}
                    error={!!error.value}
                    helperText={error.value}
                    onChange={e =>
                        this.updateItem(index,'value',e.target.value)
                    }
                    sx={{
                        flex:1
                    }}
                />

                <IconButton onClick={() => this.removeItem(index)}>
                    <Delete />
                </IconButton>
            </div>
        );
    };

    deleteItem = async item => {
        const { reqUserID } = this.getCredentials();
        const url =
            ETVConstants.getServerBase() +
            '/intercom_dtmf?action=delete_dtmf' +
            '&account_id=' + encodeURIComponent(reqUserID) +
            '&dtmf_key=' + encodeURIComponent(item.dtmf_key);

        const response = await fetch(url, {
            headers: this.getAuthHeaders()
        });
        const json = await response.json();
        if (!json.result) {
            throw new Error(json.data || 'Failed to delete DTMF item');
        }
        return json;
    };


    render() {
        const { open, onClose } = this.props;
        const { loading, saving, items } = this.state;
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {ETVConstants.trans("dtmf_settings")}
                </DialogTitle>

                <DialogContent>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <>
                            {items.map((item, index) =>
                                this.renderItem(item, index)
                            )}

                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={this.addItem}
                            >
                                {ETVConstants.trans("add_dtmf")}
                            </Button>
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>{ETVConstants.trans("cancel")}</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<Save/>}
                        onClick={this.saveAll}
                        disabled={saving || loading}>
                        {saving ? ETVConstants.trans("saving") : ETVConstants.trans("save")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default IntercomDTMFDialog;